import React, { useState, useEffect } from 'react';
import { api } from '../App';

const NOTIFICATION_TEMPLATES = [
  { id: 'welcome', title: 'Welcome Message', body: 'Welcome to 1crew3! We\'re excited to have you on board.' },
  { id: 'verification', title: 'Verification Reminder', body: 'Please complete your profile verification to start receiving job requests.' },
  { id: 'update', title: 'Platform Update', body: 'We\'ve made some improvements to the platform. Check out what\'s new!' },
  { id: 'maintenance', title: 'Scheduled Maintenance', body: 'The platform will be undergoing maintenance on [DATE]. Please plan accordingly.' },
  { id: 'custom', title: 'Custom Message', body: '' },
];

const TARGET_OPTIONS = [
  { value: 'all', label: 'All Users', icon: '👥' },
  { value: 'yacht_owners', label: 'Yacht Owners Only', icon: '⛵' },
  { value: 'helpers', label: 'Helpers Only', icon: '🚢' },
  { value: 'unverified', label: 'Unverified Helpers', icon: '⏳' },
  { value: 'specific', label: 'Specific User', icon: '👤' },
];

function Messaging() {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [target, setTarget] = useState('all');
  const [specificUserId, setSpecificUserId] = useState('');
  const [sending, setSending] = useState(false);
  const [sentHistory, setSentHistory] = useState([]);
  const [stats, setStats] = useState({ total: 0, thisMonth: 0, thisWeek: 0 });
  const [users, setUsers] = useState([]);
  const [searchUser, setSearchUser] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    loadHistory();
    loadUsers();
  }, []);

  const loadHistory = async () => {
    try {
      const response = await api('/admin/notifications/history');
      setSentHistory(response.data || []);
      setStats({
        total: response.stats?.total || 0,
        thisMonth: response.stats?.this_month || 0,
        thisWeek: response.stats?.this_week || 0,
      });
    } catch (err) {
      // History endpoint may not exist yet
      console.log('History not available');
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api('/admin/users?limit=100');
      setUsers(response.data || []);
    } catch (err) {
      console.log('Failed to load users');
    }
  };

  const selectTemplate = (template) => {
    setSelectedTemplate(template.id);
    setTitle(template.title);
    setBody(template.body);
  };

  const sendNotification = async () => {
    if (!title || !body) {
      alert('Please enter a title and message');
      return;
    }

    if (target === 'specific' && !specificUserId) {
      alert('Please select a specific user');
      return;
    }

    setSending(true);
    setLastResult(null);
    try {
      const result = await api('/admin/notifications/send', {
        method: 'POST',
        body: JSON.stringify({
          title,
          body,
          target,
          user_id: target === 'specific' ? specificUserId : null,
          send_email: sendEmail,
        }),
      });

      setLastResult(result.data);
      const emailMsg = sendEmail && result.data.emails_sent > 0
        ? ` and ${result.data.emails_sent} email(s)`
        : '';
      alert(`Success! Sent ${result.data.notifications_sent} notification(s)${emailMsg}`);
      setTitle('');
      setBody('');
      setSelectedTemplate(null);
      setTarget('all');
      setSpecificUserId('');
      loadHistory();
    } catch (err) {
      alert('Failed to send notification: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  const filteredUsers = users.filter(u =>
    searchUser &&
    (`${u.first_name} ${u.last_name}`.toLowerCase().includes(searchUser.toLowerCase()) ||
     u.email.toLowerCase().includes(searchUser.toLowerCase()))
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>Messaging</h1>
        <p style={{ color: '#666', fontSize: 14 }}>Send notifications to users</p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>Total Sent</p>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#0066cc' }}>{stats.total}</div>
        </div>
        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>This Month</p>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#28a745' }}>{stats.thisMonth}</div>
        </div>
        <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>This Week</p>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#17a2b8' }}>{stats.thisWeek}</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Compose Section */}
        <div>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: 20 }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 16, fontWeight: 600 }}>Compose Message</h3>

            {/* Templates */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, color: '#666', marginBottom: 8 }}>Quick Templates</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {NOTIFICATION_TEMPLATES.map(template => (
                  <button
                    key={template.id}
                    onClick={() => selectTemplate(template)}
                    style={{
                      padding: '8px 12px',
                      background: selectedTemplate === template.id ? '#0066cc' : '#f0f0f0',
                      color: selectedTemplate === template.id ? 'white' : '#333',
                      border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer'
                    }}
                  >
                    {template.title}
                  </button>
                ))}
              </div>
            </div>

            {/* Target */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, color: '#666', marginBottom: 8 }}>Send To</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                {TARGET_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setTarget(opt.value)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '12px', background: target === opt.value ? '#0066cc15' : '#f8f9fa',
                      border: target === opt.value ? '2px solid #0066cc' : '2px solid transparent',
                      borderRadius: 8, cursor: 'pointer', textAlign: 'left'
                    }}
                  >
                    <span style={{ fontSize: 18 }}>{opt.icon}</span>
                    <span style={{ fontSize: 13, fontWeight: target === opt.value ? 600 : 400 }}>{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Specific User Search */}
            {target === 'specific' && (
              <div style={{ marginBottom: 20, position: 'relative' }}>
                <label style={{ display: 'block', fontSize: 13, color: '#666', marginBottom: 8 }}>Search User</label>
                <input
                  type="text"
                  placeholder="Type to search users..."
                  value={searchUser}
                  onChange={e => { setSearchUser(e.target.value); setShowUserDropdown(true); }}
                  onFocus={() => setShowUserDropdown(true)}
                  style={{
                    width: '100%', padding: '10px 14px', border: '1px solid #ddd',
                    borderRadius: 8, fontSize: 14
                  }}
                />
                {showUserDropdown && filteredUsers.length > 0 && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0,
                    background: 'white', border: '1px solid #ddd', borderRadius: 8,
                    maxHeight: 200, overflow: 'auto', zIndex: 10,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}>
                    {filteredUsers.map(user => (
                      <div
                        key={user.id}
                        onClick={() => {
                          setSpecificUserId(user.id);
                          setSearchUser(`${user.first_name} ${user.last_name}`);
                          setShowUserDropdown(false);
                        }}
                        style={{
                          padding: '10px 14px', cursor: 'pointer',
                          borderBottom: '1px solid #eee',
                          ':hover': { background: '#f8f9fa' }
                        }}
                      >
                        <div style={{ fontWeight: 500, fontSize: 14 }}>{user.first_name} {user.last_name}</div>
                        <div style={{ fontSize: 12, color: '#666' }}>{user.email}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Title */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, color: '#666', marginBottom: 8 }}>Title</label>
              <input
                type="text"
                placeholder="Notification title..."
                value={title}
                onChange={e => setTitle(e.target.value)}
                style={{
                  width: '100%', padding: '10px 14px', border: '1px solid #ddd',
                  borderRadius: 8, fontSize: 14
                }}
              />
            </div>

            {/* Body */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, color: '#666', marginBottom: 8 }}>Message</label>
              <textarea
                placeholder="Type your message here..."
                value={body}
                onChange={e => setBody(e.target.value)}
                rows={5}
                style={{
                  width: '100%', padding: '10px 14px', border: '1px solid #ddd',
                  borderRadius: 8, fontSize: 14, resize: 'vertical'
                }}
              />
            </div>

            {/* Email Toggle */}
            <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#f8f9fa', borderRadius: 8 }}>
              <div>
                <div style={{ fontWeight: 500, fontSize: 14 }}>Also send via Email</div>
                <div style={{ fontSize: 12, color: '#666' }}>Recipients will receive both in-app notification and email</div>
              </div>
              <label style={{ position: 'relative', display: 'inline-block', width: 50, height: 26 }}>
                <input
                  type="checkbox"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                  background: sendEmail ? '#0066cc' : '#ccc', borderRadius: 26, transition: '0.3s'
                }}>
                  <span style={{
                    position: 'absolute', content: '', height: 20, width: 20, left: sendEmail ? 26 : 3, bottom: 3,
                    background: 'white', borderRadius: '50%', transition: '0.3s'
                  }}></span>
                </span>
              </label>
            </div>

            {/* Last Result */}
            {lastResult && (
              <div style={{ marginBottom: 20, padding: '12px 16px', background: '#d4edda', borderRadius: 8, border: '1px solid #c3e6cb' }}>
                <div style={{ fontWeight: 500, fontSize: 14, color: '#155724' }}>Last Send Results</div>
                <div style={{ fontSize: 13, color: '#155724', marginTop: 4 }}>
                  {lastResult.notifications_sent} in-app notification(s)
                  {lastResult.emails_sent > 0 && `, ${lastResult.emails_sent} email(s)`}
                </div>
                {lastResult.email_errors && lastResult.email_errors.length > 0 && (
                  <div style={{ fontSize: 12, color: '#856404', marginTop: 4 }}>
                    {lastResult.email_errors.length} email(s) failed
                  </div>
                )}
              </div>
            )}

            {/* Send Button */}
            <button
              onClick={sendNotification}
              disabled={sending || !title || !body}
              style={{
                width: '100%', padding: '14px',
                background: sending || !title || !body ? '#ccc' : '#0066cc',
                color: 'white', border: 'none', borderRadius: 8,
                fontSize: 14, fontWeight: 600, cursor: sending || !title || !body ? 'not-allowed' : 'pointer'
              }}
            >
              {sending ? 'Sending...' : sendEmail ? 'Send Notification + Email' : 'Send Notification Only'}
            </button>
          </div>

          {/* Preview */}
          {(title || body) && (
            <div style={{ background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: 14, fontWeight: 600 }}>Preview</h4>
              <div style={{ padding: 16, background: '#f8f9fa', borderRadius: 8, border: '1px solid #eee' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: 8, background: '#0066cc',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                  }}>
                    <span style={{ color: 'white', fontSize: 18 }}>⚓</span>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{title || 'Notification Title'}</div>
                    <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>{body || 'Message content...'}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* History Section */}
        <div style={{ background: 'white', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: 16, fontWeight: 600 }}>Recent Notifications</h3>

          {sentHistory.length > 0 ? (
            <div style={{ maxHeight: 600, overflow: 'auto' }}>
              {sentHistory.map((notif, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '16px 0',
                    borderBottom: idx < sentHistory.length - 1 ? '1px solid #eee' : 'none'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{notif.title}</div>
                    <span style={{
                      padding: '4px 8px', borderRadius: 12, fontSize: 10, fontWeight: 500,
                      background: '#0066cc15', color: '#0066cc'
                    }}>
                      {notif.target}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>{notif.body}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: '#888' }}>{formatDate(notif.sent_at)}</span>
                    <span style={{ fontSize: 11, color: '#28a745' }}>
                      {notif.recipients_count || 0} recipients
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: 60, textAlign: 'center', color: '#666' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📬</div>
              <p style={{ fontSize: 16, marginBottom: 8 }}>No notifications sent yet</p>
              <p style={{ fontSize: 13 }}>Compose a message to get started</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Messaging;
